/**
 * Motor de puntuacion (seccion 8 de la especificacion).
 *
 *   PUNTOS = round( D x R x M x B )
 *
 *   D = coeficiente de dificultad del ejercicio (1.0 - 8.0)
 *   R = rendimiento NORMALIZADO contra la marca de referencia del ejercicio
 *   M = multiplicador de lastre, SOLO donde se cuentan repeticiones
 *   B = producto de las bonificaciones aplicables
 *
 * Sobre la normalizacion de R
 * ---------------------------
 * La formula de la especificacion usaba R en bruto —repeticiones x 2, segundos
 * x 1— y eso invertia la clasificacion. La marca que se puede alcanzar cae en
 * picado segun sube la dificultad: 60 flexiones frente a 3 segundos de
 * victorian. D subiendo de 1.0 a 8.0 multiplica por 8; la marca dividia por 20.
 * Resultado: el movimiento mas duro de la calistenia puntuaba menos que unas
 * flexiones.
 *
 * Ahora R se mide contra la marca de referencia del ejercicio:
 *
 *   R = (R_bruto / R_referencia) x ESCALA
 *
 * Quien clava la referencia se lleva D x ESCALA. La razon entre dos ejercicios
 * pasa a ser exactamente la razon entre sus coeficientes, que es lo que D
 * siempre dijo ser y nunca fue.
 *
 * Este modulo es puro y sin dependencias del navegador: se usa tal cual en el
 * cliente (para la puntuacion provisional que se ensena al instante) y se
 * replica en la Edge Function `score-submission`, que es la unica autoridad
 * (RNF-06: toda la logica de puntuacion se valida en servidor).
 */

import {
  BONUSES,
  DECLARED_LIMITS,
  MIN_REFERENCE_MARK,
  NORMALIZED_SCALE,
  PERFORMANCE_FACTORS,
  WEIGHTED_THEMES,
  WEIGHT_MULTIPLIER,
  type BonusCode,
} from '@/config/scoring';
import type { CompetitionTheme, EnduranceFormat, ScoreBreakdown } from '@/types/domain';

// ---------------------------------------------------------------------------
// M — multiplicador de lastre
// ---------------------------------------------------------------------------

/**
 * Tematicas en las que el lastre puntua: las que cuentan repeticiones. En un
 * aguante el lastre no entra, porque cargar y aguantar miden lo mismo y la
 * puntuacion acabaria pagando dos veces la misma cualidad.
 */
export function themeAllowsWeight(theme: CompetitionTheme): boolean {
  return WEIGHTED_THEMES.includes(theme);
}

/**
 * M = 1 + (kg_lastre / peso_corporal) x COEFFICIENT, topado (RF-PUNT-06).
 * Devuelve 1 en cuanto la tematica no cuenta repeticiones, aunque llegue un
 * lastre: asi un cliente manipulado no puede inflar la nota. En estaticos NO
 * hay lastre, sea cual sea el ejercicio.
 */
export function computeWeightMultiplier(
  addedWeightKg: number | null | undefined,
  bodyWeightKg: number,
  theme: CompetitionTheme,
): number {
  if (!themeAllowsWeight(theme)) return WEIGHT_MULTIPLIER.NEUTRAL;
  const added = addedWeightKg ?? 0;
  if (added <= 0) return WEIGHT_MULTIPLIER.NEUTRAL;
  if (!Number.isFinite(bodyWeightKg) || bodyWeightKg <= 0) return WEIGHT_MULTIPLIER.NEUTRAL;

  const raw = 1 + (added / bodyWeightKg) * WEIGHT_MULTIPLIER.COEFFICIENT;
  return Math.min(raw, WEIGHT_MULTIPLIER.MAX);
}

// ---------------------------------------------------------------------------
// R — factor de rendimiento
// ---------------------------------------------------------------------------

export interface PerformanceInput {
  theme: CompetitionTheme;
  /** Repeticiones declaradas (DYNAMIC, ENDURANCE en formato AMRAP). */
  reps?: number | null;
  /** Segundos aguantados (STATIC) o empleados (ENDURANCE por tiempo). */
  seconds?: number | null;
  /** Formato de la competicion de resistencia. */
  enduranceFormat?: EnduranceFormat | null;
  /** Segundos objetivo de la competicion en formato FOR_TIME. */
  targetSeconds?: number | null;
}

/**
 * R en bruto, tal cual lo define la tabla 8.1: lo que se hizo, en la unidad de
 * la tematica. Solo es comparable entre marcas del MISMO ejercicio.
 */
export function computeRawPerformance(input: PerformanceInput): number {
  const { theme } = input;

  switch (theme) {
    case 'DYNAMIC':
    case 'DYNAMIC_WEIGHTED':
      return nonNegative(input.reps) * PERFORMANCE_FACTORS.DYNAMIC_REP_FACTOR;

    case 'STATIC':
      return nonNegative(input.seconds) * PERFORMANCE_FACTORS.STATIC_SECOND_FACTOR;

    case 'ENDURANCE': {
      if (input.enduranceFormat === 'FOR_TIME') {
        // R = (tiempo_objetivo / tiempo_empleado) x 60
        const elapsed = nonNegative(input.seconds);
        const target = nonNegative(input.targetSeconds);
        if (elapsed <= 0 || target <= 0) return 0;
        return (target / elapsed) * PERFORMANCE_FACTORS.ENDURANCE_FOR_TIME_FACTOR;
      }
      // Formato AMRAP: R = repeticiones x 0.8
      return nonNegative(input.reps) * PERFORMANCE_FACTORS.ENDURANCE_REP_FACTOR;
    }

    default:
      return 0;
  }
}

/**
 * R que corresponde a la marca de referencia, calculado por el mismo camino
 * que el del atleta para que la division compare peras con peras. En las
 * pruebas por tiempo el objetivo lo pone la competicion, asi que la referencia
 * viaja como segundos empleados y se convierte aqui igual que una marca real.
 */
export function computeReferencePerformance(
  reference: number,
  input: PerformanceInput,
): number {
  const mark = Math.max(nonNegative(reference), MIN_REFERENCE_MARK);

  switch (input.theme) {
    case 'DYNAMIC':
    case 'DYNAMIC_WEIGHTED':
      return computeRawPerformance({ ...input, reps: mark });

    case 'STATIC':
      return computeRawPerformance({ ...input, seconds: mark });

    case 'ENDURANCE':
      return input.enduranceFormat === 'FOR_TIME'
        ? computeRawPerformance({ ...input, seconds: mark })
        : computeRawPerformance({ ...input, reps: mark });

    default:
      return 0;
  }
}

/**
 * R normalizado: cuanto vale la marca frente a lo que se considera solido en
 * ese movimiento. Es el factor que entra en la formula.
 */
export function computePerformanceFactor(
  input: PerformanceInput,
  reference: number,
): number {
  const raw = computeRawPerformance(input);
  const referenceFactor = computeReferencePerformance(reference, input);
  if (referenceFactor <= 0) return 0;
  return (raw / referenceFactor) * NORMALIZED_SCALE;
}

// ---------------------------------------------------------------------------
// B — bonificaciones
// ---------------------------------------------------------------------------

/** B es el producto de todas las bonificaciones aplicables (tabla 8.1). */
export function computeBonusTotal(codes: readonly BonusCode[]): {
  total: number;
  applied: { code: string; value: number }[];
} {
  const applied: { code: string; value: number }[] = [];
  let total = 1;
  // Un mismo bonus no se aplica dos veces aunque llegue duplicado.
  for (const code of new Set(codes)) {
    const bonus = BONUSES[code];
    if (!bonus) continue;
    total *= bonus.value;
    applied.push({ code: bonus.code, value: bonus.value });
  }
  return { total, applied };
}

// ---------------------------------------------------------------------------
// Puntuacion completa
// ---------------------------------------------------------------------------

export interface ScoreInput {
  /** D — coeficiente del ejercicio, congelado en el envio (RF-PUNT-02). */
  difficulty: number;
  /**
   * Marca de referencia del ejercicio, congelada en el envio igual que D: si
   * manana se recalibra, las puntuaciones ya dadas no se mueven (RF-PUNT-02).
   */
  reference: number;
  theme: CompetitionTheme;
  performance: PerformanceInput;
  addedWeightKg?: number | null;
  bodyWeightKg: number;
  bonuses?: readonly BonusCode[];
  /** Multiplicador propio de la competicion: 2 en la Semana de las Termópilas. */
  competitionMultiplier?: number;
  /** Ajuste manual del administrador en puntos, con motivo (RF-PUNT-04). */
  manualAdjustment?: number | null;
  manualAdjustmentReason?: string | null;
}

/**
 * Calcula la puntuacion y devuelve el desglose completo, que se guarda en
 * `submissions.score_breakdown_json` y se ensena al atleta (RF-PUNT-03).
 */
export function computeScore(input: ScoreInput): ScoreBreakdown {
  const difficulty = Math.max(0, input.difficulty);
  const rawPerformance = computeRawPerformance(input.performance);
  const referenceFactor = computeReferencePerformance(input.reference, input.performance);
  const performance = referenceFactor > 0 ? (rawPerformance / referenceFactor) * NORMALIZED_SCALE : 0;
  const weightMultiplier = computeWeightMultiplier(
    input.addedWeightKg,
    input.bodyWeightKg,
    input.theme,
  );
  const { total: bonusTotal, applied } = computeBonusTotal(input.bonuses ?? []);
  const competitionMultiplier = input.competitionMultiplier ?? 1;

  const base = difficulty * performance * weightMultiplier * bonusTotal * competitionMultiplier;
  const adjustment = input.manualAdjustment ?? 0;
  const total = Math.max(0, Math.round(base + adjustment));

  return {
    difficulty,
    performance,
    rawPerformance,
    referenceFactor,
    referenceMark: input.reference,
    weightMultiplier,
    bonusTotal,
    bonuses: applied,
    competitionMultiplier,
    manualAdjustment: input.manualAdjustment ?? null,
    manualAdjustmentReason: input.manualAdjustmentReason ?? null,
    total,
  };
}

// ---------------------------------------------------------------------------
// Validacion del envio
// ---------------------------------------------------------------------------

export type SubmissionValidationError =
  | 'REPS_REQUIRED'
  | 'REPS_OUT_OF_RANGE'
  | 'SECONDS_REQUIRED'
  | 'SECONDS_OUT_OF_RANGE'
  | 'WEIGHT_NOT_ALLOWED'
  | 'WEIGHT_REQUIRED'
  | 'WEIGHT_OUT_OF_RANGE'
  | 'BODY_WEIGHT_OUT_OF_RANGE'
  | 'EXERCISE_NOT_ALLOWED';

export interface SubmissionValidationInput {
  theme: CompetitionTheme;
  enduranceFormat?: EnduranceFormat | null;
  requiresWeight: boolean;
  allowsWeight: boolean;
  reps?: number | null;
  seconds?: number | null;
  addedWeightKg?: number | null;
  bodyWeightKg: number;
  exerciseId: string;
  allowedExerciseIds: readonly string[];
}

/**
 * Valida un envio antes de puntuarlo. La misma funcion corre en el formulario
 * y en la Edge Function, para que el mensaje que ve el atleta y la regla que
 * aplica el servidor no puedan divergir.
 */
export function validateSubmission(
  input: SubmissionValidationInput,
): SubmissionValidationError[] {
  const errors: SubmissionValidationError[] = [];
  const {
    theme,
    reps,
    seconds,
    addedWeightKg,
    bodyWeightKg,
    requiresWeight,
    allowsWeight,
    exerciseId,
    allowedExerciseIds,
  } = input;

  if (allowedExerciseIds.length > 0 && !allowedExerciseIds.includes(exerciseId)) {
    errors.push('EXERCISE_NOT_ALLOWED');
  }

  const needsReps =
    theme === 'DYNAMIC' ||
    theme === 'DYNAMIC_WEIGHTED' ||
    (theme === 'ENDURANCE' && input.enduranceFormat !== 'FOR_TIME');
  const needsSeconds =
    theme === 'STATIC' || (theme === 'ENDURANCE' && input.enduranceFormat === 'FOR_TIME');

  if (needsReps) {
    if (reps == null) errors.push('REPS_REQUIRED');
    else if (reps < DECLARED_LIMITS.MIN_REPS || reps > DECLARED_LIMITS.MAX_REPS) {
      errors.push('REPS_OUT_OF_RANGE');
    }
  }

  if (needsSeconds) {
    if (seconds == null) errors.push('SECONDS_REQUIRED');
    else if (seconds < DECLARED_LIMITS.MIN_SECONDS || seconds > DECLARED_LIMITS.MAX_SECONDS) {
      errors.push('SECONDS_OUT_OF_RANGE');
    }
  }

  // Fuera de las tematicas de repeticiones el lastre no existe.
  const weightPermitted = themeAllowsWeight(theme) && allowsWeight;
  const declaredWeight = addedWeightKg ?? 0;

  if (!weightPermitted && declaredWeight > 0) {
    errors.push('WEIGHT_NOT_ALLOWED');
  }
  // RF-COMP-09: en DYNAMIC_WEIGHTED el lastre es obligatorio.
  if (weightPermitted && requiresWeight && declaredWeight <= 0) {
    errors.push('WEIGHT_REQUIRED');
  }
  if (
    declaredWeight > DECLARED_LIMITS.MAX_ADDED_WEIGHT_KG ||
    declaredWeight < DECLARED_LIMITS.MIN_ADDED_WEIGHT_KG
  ) {
    errors.push('WEIGHT_OUT_OF_RANGE');
  }

  if (
    bodyWeightKg < DECLARED_LIMITS.MIN_BODY_WEIGHT_KG ||
    bodyWeightKg > DECLARED_LIMITS.MAX_BODY_WEIGHT_KG
  ) {
    errors.push('BODY_WEIGHT_OUT_OF_RANGE');
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function nonNegative(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value < 0) return 0;
  return value;
}


/** Redondeo a 3 decimales para ensenar M y B sin ruido de coma flotante. */
export function roundFactor(value: number): number {
  return Math.round(value * 1000) / 1000;
}
